import { apiFetch } from '@/api/client';
import { uploadItemMedia } from '@/api/upload';
import type { AddItemUnitBatchResult, Item, ItemAttribute } from '@core/api/masters';
import {
  linkStockImportDesigns,
  sendStockImportBatch,
  startStockImport,
  type StockImport,
} from '@core/api/stockImport';
import {
  importBatches,
  withAddedValues,
  type ImportPlan,
  type NewDesign,
  type NewValue,
  type WordMap,
} from '@core/units/stockImport';

import { valueCode } from './importRules';

/**
 * Running an import, in the canvas's order: the new values, then the designs, then the garments in
 * batches, then the photos. Each step goes through the endpoint a person would use by hand — the
 * attribute's values, the design's create, the delivery — so an imported garment is booked in
 * exactly as a typed one.
 *
 * <p>
 * <strong>A batch is all or nothing, and the run stops at the first refusal.</strong> What came in
 * before it stays in and is recorded against the import, so running the same sheet again skips those
 * rows and carries on — and Undo takes the whole import back while nothing has happened to it.
 * </p>
 */

export interface RunProgress {
  stage: 'starting' | 'values' | 'designs' | 'garments' | 'photos' | 'done';
  designs: { done: number; total: number };
  garments: { done: number; total: number };
  photos: { done: number; total: number };
}

export interface RunInput {
  plan: ImportPlan;
  words: WordMap;
  /** The plan again with these choices — after new values exist, their rows carry real ids. */
  planAgain: (words: WordMap) => ImportPlan;
  source: { fileName: string; sheetName: string | null; rowsRead: number };
  delivery: { purchasedOn: string; supplierVendorId: number | null; note: string };
  /** A row's picture key → its file: pictures in the sheet, and the photo files chosen. */
  photo: (key: string) => File | undefined;
  onProgress: (progress: RunProgress) => void;
}

export interface RunResult {
  importRecord: StockImport | null;
  /** The plan the garments were booked from — with real ids for every new value. */
  plan: ImportPlan;
  designIds: Record<string, number>;
  batches: AddItemUnitBatchResult[];
  /** Each batch's rows, in order, beside its result — which garments came from which row. */
  batchRows: number[][];
  photoFailures: { design: NewDesign; designId: number; file: File }[];
  /** Why the run stopped early, in the server's words; null when it finished. */
  stopped: string | null;
}

const message = (error: unknown) =>
  error instanceof Error && error.message ? error.message : 'The server did not answer.';

export async function runImport(input: RunInput): Promise<RunResult> {
  const progress: RunProgress = {
    stage: 'starting',
    designs: { done: 0, total: input.plan.newDesigns.length },
    garments: { done: 0, total: input.plan.summary.garments },
    photos: { done: 0, total: 0 },
  };
  const report = () => input.onProgress({ ...progress });
  const result: RunResult = {
    importRecord: null,
    plan: input.plan,
    designIds: {},
    batches: [],
    batchRows: [],
    photoFailures: [],
    stopped: null,
  };

  report();

  try {
    result.importRecord = await startStockImport(input.source);
  } catch (error) {
    return { ...result, stopped: message(error) };
  }

  const importId = result.importRecord.id;

  // 1. The new values — sizes and colours the person chose to add.
  if (input.plan.newValues.length > 0) {
    progress.stage = 'values';
    report();

    const created: Record<number, number> = {};

    for (const value of input.plan.newValues) {
      try {
        created[value.placeholderId] = await addValue(value);
      } catch (error) {
        return {
          ...result,
          stopped: `${value.attributeName} “${value.word}” could not be added: ${message(error)}`,
        };
      }
    }

    result.plan = input.planAgain(withAddedValues(input.words, input.plan.newValues, created));
  }

  // 2. The designs, through the ordinary create; then tied to the import so Undo takes them back.
  progress.stage = 'designs';
  report();

  for (const design of result.plan.newDesigns) {
    try {
      const link = design.picture?.startsWith('url:') ? design.picture.slice(4) : null;
      const written = await apiFetch<Item>('/api/item', {
        method: 'POST',
        body: {
          code: design.code,
          name: design.name,
          categoryId: design.categoryId,
          shared: false,
          primaryImageUrl: link,
          salePrice: design.salePrice,
        },
      });

      result.designIds[design.key] = written.id;
      progress.designs.done += 1;
      report();
    } catch (error) {
      await linkDesigns(importId, result);

      return { ...result, stopped: `Design “${design.name}” could not be made: ${message(error)}` };
    }
  }

  await linkDesigns(importId, result);

  // 3. The garments, a hundred at a time, each batch whole or not at all.
  progress.stage = 'garments';
  report();

  for (const batch of importBatches(result.plan, result.designIds, input.delivery)) {
    try {
      const done = await sendStockImportBatch(importId, batch);

      result.batches.push(done);
      result.batchRows.push(batch.rows.map((row) => row.number));
      progress.garments.done += done.totalUnits;
      report();
    } catch (error) {
      return { ...result, stopped: message(error) };
    }
  }

  // 4. The photos of the new designs. A photo that fails is said; the stock is in either way.
  const photos = result.plan.newDesigns.flatMap((design) => {
    const file =
      design.picture && !design.picture.startsWith('url:')
        ? input.photo(design.picture)
        : undefined;
    const designId = result.designIds[design.key];

    return file && designId !== undefined ? [{ design, designId, file }] : [];
  });

  progress.stage = 'photos';
  progress.photos.total = photos.length;
  report();
  result.photoFailures = await sendPhotos(photos, () => {
    progress.photos.done += 1;
    report();
  });

  progress.stage = 'done';
  report();

  return result;
}

/** Photos sent one at a time, as the design page sends them; the ones that failed come back. */
export async function sendPhotos(
  photos: RunResult['photoFailures'],
  onSent: () => void = () => undefined,
): Promise<RunResult['photoFailures']> {
  const failed: RunResult['photoFailures'] = [];

  for (const photo of photos) {
    try {
      await uploadItemMedia(photo.designId, photo.file);
    } catch {
      failed.push(photo);
    }

    onSent();
  }

  return failed;
}

async function addValue(value: NewValue): Promise<number> {
  const code = valueCode(value.word);
  const numeric = Number(value.word.replace(/["”]/g, '').trim());
  const attribute = await apiFetch<ItemAttribute>(
    `/api/item-attribute/${value.attributeId}/values`,
    {
      method: 'POST',
      body: {
        code,
        name: value.word.trim(),
        numericValue: Number.isFinite(numeric) ? numeric : null,
      },
    },
  );
  const added = attribute.values.find((candidate) => candidate.code.toUpperCase() === code);

  if (!added) {
    throw new Error('the new value was not in the answer');
  }

  return added.id;
}

async function linkDesigns(importId: number, result: RunResult): Promise<void> {
  const ids = Object.values(result.designIds);

  if (ids.length > 0) {
    result.importRecord = await linkStockImportDesigns(importId, ids).catch(
      () => result.importRecord,
    );
  }
}
